import React, { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import {
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine
} from "recharts";

import TaskForm from "./TaskForm";
import CustomSelect from "./common/CustomSelect";

function Dashboard({ tasks = [], teams = [], subTeams = [], departments = [], onCreateTask, onUpdateTask, users = [], onRefreshUsers, darkMode }) {
  const { currentUser, loading } = useUser();
  const navigate = useNavigate();
  const [search, setSearch] = React.useState("");
  const [teamSearch, setTeamSearch] = React.useState("");
  const [selectedTeam, setSelectedTeam] = React.useState(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [editingTask, setEditingTask] = React.useState(null);
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);


  const isAdmin = currentUser && (currentUser.role === "Global Admin" || currentUser.role === "Operational Admin");
  const isDeptAdmin = currentUser && (currentUser.role === "Department Admin" || (currentUser.adminDeptIds && currentUser.adminDeptIds.length > 0));

  const getTaskAssignees = useCallback((task) => {
    if (!Array.isArray(task?.task_assignees)) return [];

    return task.task_assignees
      .map((a) => {
        const profile = Array.isArray(a.profiles) ? a.profiles[0] : a.profiles;
        const matchedUser = users.find((u) => u.id === a.user_id);
        const finalUser = profile || matchedUser;

        if (!finalUser) return null;

        return {
          id: finalUser.id || a.user_id,
          name: finalUser.name || "",
          email: finalUser.email || "",
          team_id: finalUser.team_id || "",
          department_id: finalUser.department_id || "",
          role: finalUser.role || "",
          sub_team_ids: finalUser.sub_team_ids || [],
        };
      })
      .filter(Boolean);
  }, [users]);

  const getAssigneeDisplay = useCallback((task) => {
    const assignees = getTaskAssignees(task);
    if (!assignees.length) return "Unassigned";
    return assignees
      .map((a) => a.name || a.email)
      .filter(Boolean)
      .join(", ");
  }, [getTaskAssignees]);

  const isAssignedToCurrentUser = useCallback((task) => {
    return getTaskAssignees(task).some(
      (u) =>
        u.id === currentUser?.id ||
        u.email?.toLowerCase() === currentUser?.email?.toLowerCase()
    );
  }, [getTaskAssignees, currentUser]);

  const visibleTasks = useMemo(() => {
    if (!Array.isArray(tasks)) return [];
    let filtered = tasks.filter((t) => {
      if (t.deleted_at) return false;
      if (t.task_type === "loop" && !t.is_loop_occurrence) return false;
      if (t.is_loop_occurrence) {
        const occurrenceStartIso = t.start_date ? String(t.start_date).slice(0, 10) : "";
        const todayIso = new Date().toISOString().slice(0, 10);
        if (occurrenceStartIso > todayIso) return false;
      }
      return true;
    });
    if (!isAdmin && !isDeptAdmin) {
      filtered = filtered.filter((t) => isAssignedToCurrentUser(t));
    } else if (selectedTeam) {
      filtered = filtered.filter(t => {
        if (t.team_id === selectedTeam.id) return true;
        const assignees = getTaskAssignees(t);
        return assignees.some(u => u.team_id === selectedTeam.id);
      });
    } else {
      const allowedTeamIds = currentUser?.allowedTeamIds || [];
      const adminDeptIds = currentUser?.adminDeptIds || [];

      if (isAdmin) {
        filtered = [...filtered];
      } else if (isDeptAdmin) {
        // Restricted sub-team resolution for Department Admins
        const mySubTeamIds = currentUser.sub_team_ids || [];
        const hasSubTeams = mySubTeamIds.length > 0;

        filtered = filtered.filter(t => {
          // 1. Tasks assigned to self
          const isSelf = isAssignedToCurrentUser(t);
          if (isSelf) return true;

          // 2. Direct membership in task's team
          if (allowedTeamIds.includes(t.team_id)) return true;

          // 3. Department Admin scope: check if task's team belongs to a managed department
          const taskTeam = teams.find(tm => tm.id === t.team_id);
          const inAdminDept = taskTeam && adminDeptIds.includes(taskTeam.department_id);
          if (inAdminDept) return true;

          // 4. Fallback checking task assignee's department/sub-team
          const assignees = getTaskAssignees(t);
          if (assignees.length === 0) {
            if (hasSubTeams && t.sub_team_id && mySubTeamIds.includes(t.sub_team_id)) return true;
            return false;
          }

          return assignees.some(assignee => {
            const isRegularUser = assignee.role === "User";

            if (hasSubTeams) {
              const shareSubTeam = assignee.sub_team_ids?.some(sid => mySubTeamIds.includes(sid));
              const taskInMySubTeam = t.sub_team_id && mySubTeamIds.includes(t.sub_team_id);
              return isRegularUser && (shareSubTeam || taskInMySubTeam);
            } else {
              const assigneeDeptId = assignee.department_id || teams.find(tm => tm.id === assignee.team_id)?.department_id;
              const sameDept = adminDeptIds.includes(assigneeDeptId);
              return isRegularUser && sameDept;
            }
          });
        });
      } else {
        // Fallback for any other roles: show tasks in their teams plus personal tasks
        filtered = filtered.filter((t) => isAssignedToCurrentUser(t) || allowedTeamIds.includes(t.team_id));
      }
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(t => {
        const title = (t.title || "").toLowerCase();
        const assigneeName = getTaskAssignees(t)
          .map((u) => u.name || u.email)
          .join(", ")
          .toLowerCase();
        return title.includes(q) || assigneeName.includes(q);
      });
    }

    const dedupedMap = new Map();
    filtered.forEach(t => {
      if (t.is_loop_occurrence || t.task_type === "loop") {
        const parentId = t.loop_series_id || t.parent_task_id || t.id;
        const dateKey = t.start_date ? String(t.start_date).slice(0, 10) : "nodate";
        const key = `${parentId}_${dateKey}`;
        if (!dedupedMap.has(key)) {
          dedupedMap.set(key, t);
        }
      } else {
        dedupedMap.set(t.id, t);
      }
    });

    return Array.from(dedupedMap.values());
  }, [tasks, isAdmin, isDeptAdmin, currentUser, search, selectedTeam, teams, getTaskAssignees, isAssignedToCurrentUser]);

  const [todoFilter, setTodoFilter] = React.useState("today");

  const pieData = useMemo(() => {
    // Both Admins and Users now use the filtered visibleTasks as the source for the pie chart.
    // This ensures cards and charts are always in sync.
    const activeTasks = visibleTasks.filter(t => {
      const s = (t.status || "").toString().trim().toLowerCase();
      return s !== "completed";
    });

    if (activeTasks.length === 0) return [];

    const inProgressCount = activeTasks.filter(t => {
      const s = (t.status || "").toString().trim().toLowerCase();
      return s === "in progress";
    }).length;

    const todoCount = Math.max(0, activeTasks.length - inProgressCount);

    return [
      { name: "In Progress", value: inProgressCount, color: "#f59e0b" },
      { name: "Remaining", value: todoCount, color: "#e5e7eb" }
    ];
  }, [visibleTasks]);

  const todoTasks = useMemo(() => {
    const n = new Date();
    const localYear = n.getFullYear();
    const localMonth = String(n.getMonth() + 1).padStart(2, '0');
    const localDay = String(n.getDate()).padStart(2, '0');
    const todayStr = `${localYear}-${localMonth}-${localDay}`;


    const getTaskDateStr = (t) => {

      const d = t.startDate || t.start_date || t.dueDate || t.due_date;
      if (!d) return null;
      const s = String(d);

      const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) return match[0];

      try {
        const temp = new Date(d);
        if (Number.isNaN(temp.getTime())) return null;
        const ly = temp.getFullYear();
        const lm = String(temp.getMonth() + 1).padStart(2, '0');
        const ld = String(temp.getDate()).padStart(2, '0');
        return `${ly}-${lm}-${ld}`;
      } catch {
        return null;
      }
    };



    const todayStart = new Date(localYear, n.getMonth(), n.getDate());
    const rangeEnd = new Date(todayStart);
    const rangeStart = new Date(todayStart);

    if (todoFilter === "week") {

      const day = todayStart.getDay();


      const diffEnd = day === 0 ? 0 : 7 - day;
      rangeEnd.setDate(todayStart.getDate() + diffEnd);
      rangeEnd.setHours(23, 59, 59, 999);


      const diffStart = day === 0 ? 6 : day - 1;
      rangeStart.setDate(todayStart.getDate() - diffStart);
      rangeStart.setHours(0, 0, 0, 0);
    }

    return visibleTasks.filter(t => {

      const status = (t.status || "").trim().toLowerCase();
      if (status !== "todo") return false;


      const tDateStr = getTaskDateStr(t);
      if (!tDateStr) return false;

      if (todoFilter === "today") {
        return tDateStr <= todayStr;
      } else {

        const parts = tDateStr.split('-').map(Number);
        const tDate = new Date(parts[0], parts[1] - 1, parts[2]);
        tDate.setHours(0, 0, 0, 0);
        return tDate >= rangeStart && tDate <= rangeEnd;
      }
    });
  }, [visibleTasks, todoFilter]);

  const stats = useMemo(() => {
    const s = {
      total: visibleTasks.length,
      todo: 0,
      inProgress: 0,
      completed: 0,
      overdue: 0
    };
    const now = new Date().toISOString().slice(0, 10);

    visibleTasks.forEach((t) => {
      const normStatus = (t.status || "").toString().trim().toLowerCase();
      let statusKey = "todo";
      if (normStatus === "in progress") statusKey = "in progress";
      else if (normStatus === "completed") statusKey = "completed";

      if (statusKey === "in progress") s.inProgress++;
      else if (statusKey === "completed") s.completed++;
      else s.todo++; // Treat everything else as Todo for summary cards

      if (statusKey !== "completed" && (t.due_date || t.dueDate) && (t.due_date || t.dueDate) < now) {
        s.overdue++;
      }
    });
    return s;
  }, [visibleTasks]);

  const grouped = useMemo(() => {
    const out = { Todo: [], "In Progress": [], Completed: [] };
    visibleTasks.forEach((t) => {
      if (t.status === "In Progress") out["In Progress"].push(t);
      else if (t.status === "Completed") out.Completed.push(t);
      else out.Todo.push(t);
    });
    return out;
  }, [visibleTasks]);



  const accessibleTeams = useMemo(() => {
    let baseTeams = teams;

    const allowedTeamIds = currentUser?.allowedTeamIds || [];
    const adminDeptIds = currentUser?.adminDeptIds || [];

    if (isAdmin) {
      return baseTeams;
    }

    if (isDeptAdmin) {
      return baseTeams.filter(
        t => adminDeptIds.includes(t.department_id) || allowedTeamIds.includes(t.id)
      );
    }

    if (currentUser) {
      return baseTeams.filter(t => allowedTeamIds.includes(t.id));
    }
    return [];
  }, [teams, isAdmin, isDeptAdmin, currentUser]);

  const filteredTeams = useMemo(() => {
    const base = teamSearch.trim()
      ? accessibleTeams.filter(t => t.name.toLowerCase().includes(teamSearch.toLowerCase()))
      : accessibleTeams;

    // Attach members to each team object for the UI
    return base.map(team => ({
      ...team,
      members: users.filter(u => u.team_id === team.id)
    }));
  }, [accessibleTeams, teamSearch, users]);


  React.useEffect(() => {
    if (!isAdmin && !isDeptAdmin && accessibleTeams.length > 0 && !selectedTeam) {
      // For users, default the filter to their first accessible team (usually their own)
      setSelectedTeam(accessibleTeams[0]);
    }
  }, [isAdmin, isDeptAdmin, accessibleTeams, selectedTeam]);

  const handleSaveTask = async (taskData) => {
    const now = new Date().toISOString();
    if (editingTask && onUpdateTask) {
      await onUpdateTask(editingTask.id, { ...taskData, updated_at: now });
    } else if (onCreateTask) {
      const payload = {
        ...taskData,
        created_at: now,
        updated_at: now,
        id: taskData.id || undefined
      };
      await onCreateTask(payload);
    }
    setIsModalOpen(false);
    setEditingTask(null);
  };

  if (loading) {
    return <div style={{ padding: 20 }}>Loading...</div>;
  }

  const formatDate = (d) => {
    if (!d) return "—";
    const iso = d?.toString();
    if (iso.length >= 10) return iso.slice(0, 10);
    try {
      return new Date(d).toISOString().slice(0, 10);
    } catch {
      return d;
    }
  };


  return (
    <div className="page">
      <div className="page-header" style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        marginBottom: 32,
        background: darkMode ? "#4d7a22" : "#76AB3F",
        padding: "32px",
        borderRadius: "24px",
        color: "white",
        boxShadow: darkMode ? "0 10px 25px -5px rgba(0, 0, 0, 0.4)" : "0 10px 25px -5px rgba(16, 185, 129, 0.25)",
        display: "flex",
        flexWrap: "wrap",
        transition: "all 0.3s ease"
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "white", textTransform: "uppercase", letterSpacing: "0.05em", opacity: 0.9 }}>
            {new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 800, color: "white", margin: 0, letterSpacing: "-0.02em", background: 'none' }}>
            Welcome back, <span style={{ color: "white", background: "none", textDecoration: "none" }}>{currentUser?.name ? currentUser.name : (currentUser?.email?.split('@')[0] || '')}</span>
          </h1>
          <p style={{ fontSize: 16, color: "white", opacity: 0.9, margin: 0, maxWidth: "600px", lineHeight: 1.5 }}>
            Here's an overview of your Tasks. You have <strong style={{ color: "white", opacity: 1 }}>{stats.todo} tasks to do</strong> and <strong style={{ color: "white", opacity: 1 }}>{stats.inProgress} in progress</strong>.
          </p>
        </div>

        {true && (
          <div style={{ display: "flex", alignItems: "center" }}>
            <button
              className="btn-secondary"
              onClick={() => navigate("/tasks/new")}
              style={{
                background: "white",
                color: "#76AB3F",
                fontWeight: "bold",
                border: "none",
                whiteSpace: "nowrap",
                flexShrink: 0,
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 20px",
                borderRadius: "12px",
                fontSize: "14px",
                cursor: "pointer"
              }}
            >
              <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> New Task
            </button>
          </div>
        )}
      </div>
      <div className="dashboard-summary-grid">
        <div className="dashboard-summary-card">
          <div className="dash-label">Total Tasks</div>
          <div className="dash-value">{stats.total}</div>
        </div>
        <div className="dashboard-summary-card">
          <div className="dash-label">Todo</div>
          <div className="dash-value" style={{ color: "var(--success-500)" }}>{stats.todo}</div>
        </div>
        <div className="dashboard-summary-card">
          <div className="dash-label">In Progress</div>
          <div className="dash-value" style={{ color: "var(--warning-500)" }}>{stats.inProgress}</div>
        </div>
        <div className="dashboard-summary-card">
          <div className="dash-label">Completed</div>
          <div className="dash-value" style={{ color: "var(--info-500)" }}>{stats.completed}</div>
        </div>
        <div className="dashboard-summary-card">
          <div className="dash-label">Overdue</div>
          <div className="dash-value" style={{ color: "var(--danger-text)" }}>{stats.overdue}</div>
        </div>
        {true && (
          <div
            className="dashboard-summary-card"
            onClick={() => navigate("/tasks/new")}
            style={{ cursor: "pointer", border: darkMode ? "1px solid #374151" : "1px solid var(--primary-200)", alignItems: "center" }}
            title="Create New Task"
          >
            <div className="dash-value" style={{ color: "var(--primary-600)", fontSize: 28, marginTop: 0 }}>+</div>
            <div className="dash-label" style={{ color: "var(--primary-600)", marginTop: 4 }}>New Task</div>
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, marginBottom: 24 }}>
        {/* Graph Section */}
        <div className="card" style={{ display: "flex", flexDirection: "column", paddingBottom: 0, height: 440 }}>
          <div style={{ display: "flex", gap: 24, fontSize: 13, fontWeight: 600, color: "var(--gray-700)", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--success-500)" }}></div> Todo
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--warning-500)" }}></div> In Progress
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--info-500)" }}></div> Completed
            </div>
          </div>

          <div style={{ height: 340, width: "100%", marginTop: "auto", minWidth: 0, minHeight: 0 }}>
            {mounted && (
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <BarChart
                  data={[
                    { name: "Todo", value: stats.todo, color: "#10b981" },
                    { name: "In Progress", value: stats.inProgress, color: "#f59e0b" },
                    { name: "Completed", value: stats.completed, color: "#3b82f6" }
                  ]}
                  margin={{ top: 10, right: 40, left: 10, bottom: 50 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={darkMode ? "#374151" : "#e5e7eb"} />
                  <ReferenceLine y={0} stroke={darkMode ? "#4b5563" : "var(--gray-300)"} strokeWidth={1} />
                  <XAxis
                    dataKey="name"
                    axisLine={{ stroke: darkMode ? "#374151" : "var(--gray-200)", strokeWidth: 1 }}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: darkMode ? "#9ca3af" : "var(--gray-500)", fontWeight: 500 }}
                    height={12}
                    dy={0}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: darkMode ? "#6b7280" : "var(--gray-400)" }}
                  />
                  <Tooltip
                    cursor={{ fill: 'transparent' }}
                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                  />
                  <Bar dataKey="value" radius={[10, 10, 0, 0]} barSize={60}>
                    {
                      [
                        { name: "Todo", value: stats.todo, color: "#10b981" },
                        { name: "In Progress", value: stats.inProgress, color: "#f59e0b" },
                        { name: "Completed", value: stats.completed, color: "#3b82f6" }
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))
                    }
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="card" style={{ display: "flex", flexDirection: "column", height: 440 }}>
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ margin: "0 0 12px 0", fontSize: 16, fontWeight: 700 }}>In Progress</h3>
            {(isAdmin || isDeptAdmin) && (
              <CustomSelect
                options={accessibleTeams.map(t => ({ value: t.id, label: t.name }))}
                value={selectedTeam ? selectedTeam.id : ""}
                onChange={(teamId) => {
                  const team = teams.find(t => t.id === teamId);
                  setSelectedTeam(team || null);
                }}
                placeholder="Select Team..."
                style={{ width: "100%" }}
              />
            )}
            {!isAdmin && !isDeptAdmin && (
              <p style={{ margin: 0, fontSize: 12, color: "var(--gray-400)" }}>
                {selectedTeam ? `Currently viewing ${selectedTeam.name}` : "All Accessible Tasks"}
              </p>
            )}
          </div>

          <div style={{ flex: 1, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", minWidth: 0, minHeight: 0 }}>
            {pieData.length === 0 ? (
              <div style={{ color: "var(--gray-400)", fontSize: 13, fontStyle: "italic" }}>
                No tasks found for this team
              </div>
            ) : (
              mounted && (
                <>
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        startAngle={90}
                        endAngle={-270}
                        dataKey="value"
                        stroke="none"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center"
                  }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: darkMode ? "#f9fafb" : "var(--gray-700)" }}>
                      {Math.round((pieData[0].value / (pieData[0].value + pieData[1].value)) * 100)}%
                    </div>
                    <div style={{ fontSize: 11, color: darkMode ? "#9ca3af" : "var(--gray-400)" }}>In Progress</div>
                  </div>
                </>
              )
            )}
          </div>

          {pieData.length > 0 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 24, marginTop: 16, borderTop: darkMode ? "1px solid #374151" : "1px solid var(--gray-100)", paddingTop: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#f59e0b" }}></div>
                <div style={{ fontSize: 13, color: darkMode ? "#d1d5db" : "var(--gray-600)" }}>
                  <span style={{ fontWeight: 700, color: darkMode ? "#f9fafb" : "var(--gray-900)" }}>{pieData[0].value}</span> In Progress
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", background: darkMode ? "#374151" : "#e5e7eb" }}></div>
                <div style={{ fontSize: 13, color: darkMode ? "#d1d5db" : "var(--gray-600)" }}>
                  <span style={{ fontWeight: 700, color: darkMode ? "#f9fafb" : "var(--gray-900)" }}>{pieData[1].value}</span> Remaining
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Todo List Section */}
        <div className="card" style={{ display: "flex", flexDirection: "column", height: 440 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: darkMode ? "#f9fafb" : "inherit" }}>Upcoming Todo's</h3>
            <div style={{ display: "flex", background: darkMode ? "#374151" : "var(--gray-100)", borderRadius: 8, padding: 2 }}>
              <button
                onClick={() => setTodoFilter("today")}
                style={{
                  padding: "4px 12px",
                  borderRadius: 6,
                  border: "none",
                  background: todoFilter === "today" ? (darkMode ? "#4b5563" : "#fff") : "transparent",
                  boxShadow: todoFilter === "today" ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
                  fontSize: 12,
                  fontWeight: 600,
                  color: todoFilter === "today" ? (darkMode ? "#f9fafb" : "var(--gray-900)") : (darkMode ? "#9ca3af" : "var(--gray-500)"),
                  cursor: "pointer"
                }}
              >
                Today
              </button>
              <button
                onClick={() => setTodoFilter("week")}
                style={{
                  padding: "4px 12px",
                  borderRadius: 6,
                  border: "none",
                  background: todoFilter === "week" ? (darkMode ? "#4b5563" : "#fff") : "transparent",
                  boxShadow: todoFilter === "week" ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
                  fontSize: 12,
                  fontWeight: 600,
                  color: todoFilter === "week" ? (darkMode ? "#f9fafb" : "var(--gray-900)") : (darkMode ? "#9ca3af" : "var(--gray-500)"),
                  cursor: "pointer"
                }}
              >
                This Week
              </button>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
            {todoTasks.length === 0 ? (
              <div style={{ color: "var(--gray-400)", fontSize: 13, fontStyle: "italic", textAlign: "center", marginTop: 20 }}>
                No todo tasks for {todoFilter === "today" ? "today" : "this week"}.
              </div>
            ) : (
              todoTasks.map(t => (
                <div
                  key={t.id}
                  onClick={() => navigate(`/tasks/${t.id}`)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: darkMode ? "1px solid #374151" : "1px solid var(--gray-100)", cursor: "pointer" }}
                >
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#76AB3F", flexShrink: 0 }}></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: darkMode ? "#f9fafb" : "var(--gray-700)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {t.title}
                    </div>
                    <div style={{ fontSize: 11, color: darkMode ? "#9ca3af" : "var(--gray-400)" }}>
                      Due: {t.dueDate || t.due_date ? new Date(t.dueDate || t.due_date).toLocaleDateString() : "No date"}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 24, marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontWeight: 700, color: darkMode ? "#f9fafb" : "inherit" }}>Teams</h3>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <input
              id="dashboard-search-member"
              name="searchMember"
              placeholder="Search member..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="calendar-select"
              style={{
                width: 110,
                padding: "6px 12px",
                borderRadius: 20,
                border: "none",
                background: darkMode ? "#374151" : "var(--gray-200)",
                outline: "none",
                fontSize: 13,
                fontWeight: 500,
                color: darkMode ? "#f9fafb" : "var(--gray-700)"
              }}
            />
            {selectedTeam && (
              <button
                onClick={() => setSelectedTeam(null)}
                style={{
                  background: "transparent",
                  border: "1px solid var(--gray-200)",
                  padding: "6px 12px",
                  borderRadius: 16,
                  cursor: "pointer",
                  fontSize: 12,
                  color: "var(--gray-500)"
                }}
              >
                Clear
              </button>
            )}
            <input
              id="dashboard-search-teams"
              name="searchTeams"
              placeholder="Search teams..."
              value={teamSearch}
              onChange={(e) => setTeamSearch(e.target.value)}
              className="calendar-select"
              style={{
                padding: "6px 12px",
                borderRadius: 20,
                border: "none",
                background: darkMode ? "#374151" : "var(--gray-200)",
                outline: "none",
                fontSize: 13,
                fontWeight: 500,
                color: darkMode ? "#f9fafb" : "var(--gray-700)",
                width: 110
              }}
            />
            {true && (
              <button
                onClick={() => navigate("/tasks/new")}
                className="btn-primary"
                style={{
                  padding: "6px 16px",
                  fontSize: 13,
                  borderRadius: 20,
                  display: "flex",
                  alignItems: "center",
                  gap: 6
                }}
              >
                <span style={{ fontSize: 16, fontWeight: "bold" }}>+</span> New Task
              </button>
            )}
          </div>
        </div>

        <div style={{
          display: "flex",
          gap: 16,
          overflowX: "auto",
          paddingBottom: 8,
          scrollbarWidth: "thin"
        }}>
          {filteredTeams.length > 0 ? filteredTeams.map(team => (
            <div
              key={team.id}
              onClick={() => setSelectedTeam(selectedTeam?.id === team.id ? null : team)}
              className="card"
              style={{
                minWidth: 200,
                padding: 16,
                cursor: "pointer",
                border: selectedTeam?.id === team.id ? "2px solid #76AB3F" : (darkMode ? "1px solid #374151" : "1px solid transparent"),
                background: selectedTeam?.id === team.id ? (darkMode ? "#1e3a24" : "var(--primary-50)") : (darkMode ? "#1f2937" : "#fff")
              }}
            >
              <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4, color: darkMode ? "#f9fafb" : "inherit" }}>{team.name}</div>
              <div style={{ fontSize: 12, color: darkMode ? "#9ca3af" : "var(--gray-500)" }}>{team.department}</div>
              <div style={{ fontSize: 11, color: darkMode ? "#6b7280" : "var(--gray-400)", marginTop: 8 }}>
                {(team.members || []).length} members
              </div>
            </div>
          )) : (
            <div style={{ color: "var(--gray-500)", fontSize: 13, fontStyle: "italic" }}>
              No teams found.
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24 }}>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontWeight: 800, color: darkMode ? "#f9fafb" : "inherit" }}>To Do</h3>
            <span style={{ background: darkMode ? "#374151" : "var(--gray-200)", color: darkMode ? "#f9fafb" : "inherit", padding: "2px 8px", borderRadius: 12, fontSize: 12, fontWeight: 600 }}>{grouped.Todo.length}</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {grouped.Todo.map(t => (
              <TaskCard key={t.id} task={t} formatDate={formatDate} darkMode={darkMode} getAssigneeDisplay={getAssigneeDisplay} />
            ))}
          </div>
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontWeight: 800, color: darkMode ? "#f9fafb" : "inherit" }}>In Progress</h3>
            <span style={{ background: darkMode ? "#374151" : "var(--gray-200)", color: darkMode ? "#f9fafb" : "inherit", padding: "2px 8px", borderRadius: 12, fontSize: 12, fontWeight: 600 }}>{grouped["In Progress"].length}</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {grouped["In Progress"].map(t => (
              <TaskCard key={t.id} task={t} formatDate={formatDate} darkMode={darkMode} getAssigneeDisplay={getAssigneeDisplay} />
            ))}
          </div>
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontWeight: 800, color: darkMode ? "#f9fafb" : "inherit" }}>Completed</h3>
            <span style={{ background: darkMode ? "#374151" : "var(--gray-200)", color: darkMode ? "#f9fafb" : "inherit", padding: "2px 8px", borderRadius: 12, fontSize: 12, fontWeight: 600 }}>{grouped.Completed.length}</span>
          </div>
          <div className="dash-list" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {grouped.Completed.map(t => (
              <TaskCard key={t.id} task={t} formatDate={formatDate} darkMode={darkMode} getAssigneeDisplay={getAssigneeDisplay} />
            ))}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <TaskForm
          onSave={handleSaveTask}
          onClose={() => {
            setIsModalOpen(false);
            setEditingTask(null);
          }}
          users={users}
          currentUser={currentUser}
          initial={editingTask}
          onRefreshUsers={onRefreshUsers}
          teams={teams}
          subTeams={subTeams}
          darkMode={darkMode}
        />
      )}
    </div>
  );
}

function TaskCard({ task, formatDate, darkMode, getAssigneeDisplay }) {
  const navigate = useNavigate();

  return (
    <div
      className="card"
      onClick={() => navigate(`/tasks/${task.id}`)}
      style={{
        padding: 16,
        border: darkMode ? "1px solid #374151" : "none",
        boxShadow: darkMode ? "0 4px 6px -1px rgba(0, 0, 0, 0.2)" : "0 1px 3px rgba(0,0,0,0.05)",
        cursor: "pointer",
        background: darkMode ? "#1f2937" : "#fff"
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>

        <span style={{
          fontSize: 11,
          padding: "2px 8px",
          borderRadius: 99,
          background: task.priority === "High" ? "#fee2e2" : task.priority === "Low" ? (darkMode ? "#1e3a24" : "var(--primary-50)") : "#fef3c7",
          color: task.priority === "High" ? "#b91c1c" : task.priority === "Low" ? (darkMode ? "#4ade80" : "var(--primary-700)") : "#92400e",
          fontWeight: 600
        }}>
          {task.priority}
        </span>
      </div>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: darkMode ? "#f9fafb" : "var(--gray-900)" }}>
        {task.title}
      </div>
      <div style={{ fontSize: 12, color: darkMode ? "#9ca3af" : "var(--gray-500)", marginBottom: 12 }}>
        {getAssigneeDisplay(task)}
      </div>
      <div style={{ fontSize: 11, color: darkMode ? "#6b7280" : "var(--gray-400)", display: "flex", gap: 8 }}>
        {task.start_date && <span>{formatDate(task.start_date)}</span>}
        {task.start_date && task.due_date && <span>•</span>}
        {task.due_date && <span>{formatDate(task.due_date)}</span>}
      </div>
      <div style={{ marginTop: 12 }}>
        <span style={{
          fontSize: 10,
          padding: "2px 8px",
          borderRadius: 6,
          background: task.status === "Completed" ? (darkMode ? "#1e3a24" : "var(--primary-50)") : task.status === "In Progress" ? (darkMode ? "#1e293b" : "#eef2ff") : (darkMode ? "#374151" : "var(--gray-100)"),
          color: task.status === "Completed" ? (darkMode ? "#4ade80" : "var(--primary-700)") : task.status === "In Progress" ? (darkMode ? "#818cf8" : "#4338ca") : (darkMode ? "#d1d5db" : "var(--gray-700)"),
          fontWeight: 600
        }}>
          {task.status}
        </span>
      </div>
    </div>
  );
}

export default Dashboard;
